import * as React from "react";
import "./NavigationTree2/style.scss";
import BaseModel from "../models/BaseModel";
import { useEffect, useRef, useState } from "react";

export type ShowNameMode = "always" | "never" | "onHover" | "onHoverAfterButtons" | "afterButtons";

export default function ObjectReference(props: {
  allowDrag?: boolean;
  obj: BaseModel | undefined;
  relative?: BaseModel;
  useAllwaysMaxSpace?: boolean;
  useSimpleFulltag?: boolean;
  showName?: ShowNameMode | boolean;
  showTag?: boolean;
  showFullTag?: boolean;
  showIcon?: any;
  style?: any;
  unlinkButtonStyle?: any;
  className?: string;
  onUnlink?: () => void;
  onDragEnd?: (ev: React.DragEvent) => void;
  onNavigated?: () => void;
}) {
  const {
    allowDrag,
    obj,
    relative,
    onUnlink,
    onDragEnd,
    onNavigated,
    useAllwaysMaxSpace,
    useSimpleFulltag,
    style,
    showIcon,
    showName,
    showTag,
    showFullTag,
    unlinkButtonStyle,
    className,
  } = props;

  const mainRef = useRef<HTMLDivElement>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  let xShowName: ShowNameMode = "always"; // no itx

  if (showName === true) {
    xShowName = "always"; // no itx
  } else if (showName === false) {
    xShowName = "onHover"; // no itx
  } else if (typeof showName === "string") {
    xShowName = showName;
  }

  useEffect(() => {
    if (mainRef.current) {
      // "always" is part of the normal layout and must be free to grow, otherwise it overlaps the next column.
      // Only the transient onHover/afterButtons overlay needs a frozen width with overflow:visible.
      if (xShowName === "always") {
        mainRef.current.style.width = "";
        mainRef.current.style.overflow = "";
      } else {
        // Only freeze width when there is no unlink button, to avoid the hover
        // area ending before the button becomes reachable.
        if (!onUnlink) {
          const currentWidth = mainRef.current.getBoundingClientRect().width;
          mainRef.current.style.width = currentWidth + "px";
        }
        mainRef.current.style.overflow = "visible";
      }
      mainRef.current.style.position = "relative";
    }
  });

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 200);
  };

  if (!obj) {
    return null;
  }

  let xName = obj.name;
  if (obj.isChannel) {
    xName = (obj as any).parent?.name;
  }

  let xTag = relative ? obj.getRelativeTag(relative) : obj.fullTag;

  if (useSimpleFulltag) {
    xTag = obj.simpleFullTag;
    if (relative) {
      const t1 = relative.simpleFullTag + ".";
      if (xTag.startsWith(t1)) {
        xTag = xTag.substring(t1.length);
      }
    }
  }

  if (showFullTag == false && showTag == true) {
    xTag = obj.tag;
  }

  if (showFullTag == false && showTag == false) {
    xTag = "";
  }

  if (!xTag && !obj.isButtonChannel) {
    xTag = obj.name;
    xName = "";
  }

  if (xShowName != "never" && showTag == false) {
    xTag = xName;
    xName = "";
    xShowName = "never"; // no itx
  }

  if (obj.isCabinet && obj.tag == "") {
    xTag = "-";
    xName = obj.nameOfDefaultName;
  }

  let xClass = useAllwaysMaxSpace ? "useMaxSpace" : "";
  if (useAllwaysMaxSpace && xShowName !== "always") {
    xClass = " useMaxSpaceWithoutName"; // no itx
  }

  return (
    <div
      ref={mainRef}
      className={"objReferenceOuter " + (className || "") + (isHovering ? " objReferenceOuter--hovering" : "")}
      style={style}
      // Fokus innerhalb der Referenz darf nicht die umgebende Navigatorzeile
      // auswählen und dadurch die Referenz vor dem Klick neu erzeugen.
      onFocus={(event) => event.stopPropagation()}
      onMouseEnter={onUnlink ? handleMouseEnter : undefined}
      onMouseLeave={onUnlink ? handleMouseLeave : undefined}
    >
      <div
        className={"objReference " + xClass}
        // Eigenes Fokusziel statt der Elternzeile; mousedown bleibt für Drag frei.
        tabIndex={-1}
        draggable={allowDrag ?? true}
        onDragStart={(ev: React.DragEvent) => {
          if (!allowDrag) {
            ev.stopPropagation();
            const mode = ev.shiftKey || ev.ctrlKey ? "copy" : "copyMove"; // no itx
            obj.treeViewModel.dragStart(ev, mode);
          }
        }}
        onDragEnd={(ev: React.DragEvent) => {
          ev.stopPropagation();
          if (onDragEnd) {
            onDragEnd(ev);
          }
        }}
        onClick={(ev: any) => {
          ev.stopPropagation();
          // Auch bereits ausgewählte Ziele ausdrücklich aufklappen und fokussieren.
          void obj.treeViewModel.navigateToReference(obj);
          if (onNavigated) {
            onNavigated();
          }
        }}
        // use a portal tooltip for the onHover-name, since it can be wider than the column and would otherwise get clipped
        data-tooltip-id={xShowName === "onHover" && xName ? "tv-tooltip" : undefined}
        data-tooltip-content={xShowName === "onHover" && xName ? xName : undefined}
      >
        {showIcon && typeof showIcon === "object" && showIcon}

        {showIcon == true && <div className="objectReferenceIcon">{obj.treeViewModel.getIcon("ObjectReference")}</div>}

        {`${xTag} `}

        {["never", "onHoverAfterButtons", "afterButtons"].indexOf(xShowName) === -1 && (
          <div className={xShowName !== "always" ? "name" : ""} style={{ marginLeft: "10px" }}>
            {xName}
          </div>
        )}
      </div>

      {onUnlink && (
        <button
          className="unlink button circle"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          draggable={false}
          style={{
            fontSize: "10px",
            height: "25px",
            width: "25px",
            margin: "0px",
            marginLeft: "5px",
            ...unlinkButtonStyle,
          }}
          onClick={(ev) => {
            ev.stopPropagation();
            onUnlink();
          }}
          data-tooltip-id="tv-tooltip"
          data-tooltip-content={
            state.translate("oneButton.deleteConnection", "Verbindung löschen") + ": " + (xName || xTag)
          }
          data-tooltip-place="top" // no itx
        >
          <i className="las la-unlink" />
        </button>
      )}

      {["onHoverAfterButtons", "afterButtons"].indexOf(xShowName) !== -1 && (
        <div className={xShowName == "onHoverAfterButtons" ? "name" : ""} style={{ marginLeft: "10px" }}>
          {xName}
        </div>
      )}
    </div>
  );
}
